/**
 * Chat Layout - Slack-like design
 * Main container with sidebar and message area
 */

import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { ChatThemeProvider } from '../../contexts/ChatThemeContext';
import { ChannelSidebar } from './ChannelSidebar';
import { MessageArea } from './MessageArea';
import { ChannelInfoSidebar } from './ChannelInfoSidebar';
import { ThreadSidebar } from './ThreadSidebar';
import { ChatSearch } from './ChatSearch';
import { useChatNotifications } from '../../hooks/useChatNotifications';
import './ChatLayout.css';

const { Sider, Content } = Layout;

export const ChatLayout: React.FC = () => {
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showChannelInfo, setShowChannelInfo] = useState(false);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);

  // Enable chat notifications (pass current channel to avoid notifying for messages in viewed channel)
  useChatNotifications(selectedChannelId);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  return (
    <ChatThemeProvider>
      <Layout className="chat-layout" style={{ height: '100vh' }}>
        {/* Left Sidebar - Channels */}
        <Sider
          width={260}
          collapsed={sidebarCollapsed}
          collapsible
          onCollapse={setSidebarCollapsed}
          className="chat-sidebar"
          theme="light"
        >
          <ChannelSidebar
            selectedChannelId={selectedChannelId}
            onChannelSelect={(channelId) => {
              setSelectedChannelId(channelId);
              // Close thread when switching channels
              setOpenThreadId(null);
            }}
            onSearchClick={() => setShowSearch(true)}
          />
        </Sider>

        {/* Main Content - Messages */}
        <Layout className="chat-content-area">
          <Content className="chat-content">
            <MessageArea 
              channelId={selectedChannelId} 
              showChannelInfo={showChannelInfo}
              onToggleChannelInfo={() => setShowChannelInfo(!showChannelInfo)}
              onOpenThread={setOpenThreadId}
            />
          </Content>
        </Layout>

        {/* Right Sidebar - Channel Info */}
        {selectedChannelId && showChannelInfo && !openThreadId && (
          <Sider
            width={300}
            className="channel-info-sidebar"
            theme="light"
            collapsible
            collapsed={false}
            reverseArrow
          >
            <ChannelInfoSidebar channelId={selectedChannelId} />
          </Sider>
        )}

        {/* Thread Sidebar */}
        {selectedChannelId && openThreadId && (
          <ThreadSidebar
            channelId={selectedChannelId}
            rootMessageId={openThreadId}
            onClose={() => setOpenThreadId(null)}
          />
        )}

        {/* Global Search */}
        <ChatSearch
          open={showSearch}
          onClose={() => setShowSearch(false)}
          onSelectChannel={(channelId) => {
            setSelectedChannelId(channelId);
            setShowSearch(false);
          }}
        />
      </Layout>
    </ChatThemeProvider>
  );
};

